import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CalendarClock,
  BellRing,
  ChevronDown,
  ChevronRight,
  Copy,
  CreditCard,
  Download,
  Eye,
  FileText,
  MailCheck,
  PauseCircle,
  PencilLine,
  PlayCircle,
  Printer,
  Send,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react';
import {
  BackLink,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  InfoHint,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  StatusPill,
  invoiceStatusToPill,
  toast,
  useReturnOrigin,
  withReturn,
  type DataTableColumn,
} from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { displayHoursUnit } from './hours-unit';
import { formatDate, formatHours, formatMoney } from '../../lib/format';
import { EditedByMark } from '../time/authorship';
import {
  describeError,
  downloadInvoicePdf,
  useBilledWork,
  useDeleteInvoice,
  useDuplicateInvoice,
  useInvoice,
  useInvoiceDocument,
  useLogReminder,
  useRemovePayment,
  useRemoveReminder,
  useSendInvoice,
  useSetRecipients,
  useVoidInvoice,
  type InvoiceDetail,
  type InvoiceDocument,
  type InvoiceLineItem,
  type InvoicePayment,
} from './api';
import {
  CHANNEL_LABELS,
  CHANNEL_VERBS,
  chaseLabel,
  followUpLabel,
  sentLabel,
} from './chase';
import { chaseReasonPhrase, useRunChaseNow, useSetChaseHold } from './chase-reminders';
import { ComposerModal, invoiceTerms, plural, type ComposerMode } from './composer';
import { InvoiceSheet } from './InvoiceSheet';
import { ScheduleSendDialog, ScheduledSendNotice } from './ScheduleSendDialog';
import { METHOD_LABELS, RecordPaymentModal } from './payment-modal';
import { LineAgreementLink } from './LineAgreementLink';
import { LineBankAction } from './LineBankAction';
import { invoiceListHref } from './list-view';

/**
 * THE PER-INVOICE OVERRIDE OF THE AUTOMATIC CHASE.
 *
 * The organization schedule ("Monday 9:00 AM, every 7 days, stopping after 4")
 * is all-or-nothing, and the objection an owner actually has is never "stop
 * chasing everybody" — it is *this* client is on a payment plan, or disputing
 * the scope, or already on the phone with me. Until this existed the only ways
 * to prevent one unwanted email were to switch automatic chasing off for every
 * other client, or to let it go out.
 *
 * Jesse's rule is that closing a gap must not add a step, so holding is ONE
 * click and the reason is optional — typed inline afterwards, never a modal
 * standing between the decision and the effect.
 *
 * ⚠ It stops the SCHEDULER, not the human: "Send reminder" in the header stays
 * live on a held invoice, because you held it in order to handle it yourself.
 * The card says so out loud rather than leaving the reader to infer it.
 */
/**
 * ⚠ **A CHASE THAT FAILED SAYS SO ON THE INVOICE, AND OFFERS THE RETRY THERE.**
 *
 * An automatic chase that lands writes an `InvoiceReminder` and shows up in the
 * Activity feed. One that fails writes nothing at all — so before this the only
 * trace of a failed run was in the response body of a cron call nobody reads,
 * while the queue behaved as though the client had been chased. The invoice is
 * the screen a person opens when they want to do something about an unpaid
 * invoice, so the failure and its remedy belong here.
 *
 * The retry is the SAME call the scheduler makes (`/invoices/reminders/run`
 * with this one id) — one click, no new screen, no second sending path.
 */
function AutomaticChaseFailure({ inv }: { inv: InvoiceDetail }) {
  const run = useRunChaseNow();
  const attempt = inv.chase.autoAttempt;
  if (!inv.chase.autoFailed || !attempt) return null;

  const retry = async () => {
    const result = await run.mutateAsync({ ids: [inv.id] });
    if (result.chased > 0) {
      toast.success(
        `#${inv.number} chased${result.mode === 'live' ? '' : ' — written to .mail, not transmitted'}.`,
      );
      return;
    }
    const why = chaseReasonPhrase(result.reason ?? result.overview?.lastFailureReason);
    toast.error(why ? `${why} — still not sent.` : 'It still did not reach the client.');
  };

  return (
    <div className="mt-3 rounded-lg border border-status-late/30 bg-status-late/5 p-3">
      <p className="flex items-start gap-2 text-body-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-late" aria-hidden />
        <span className="min-w-0">
          <span className="font-bold text-status-late">
            The automatic chase did not reach the client.
          </span>{' '}
          <span className="text-grey-600">
            {attempt.attempts === 1 ? 'One try' : `${attempt.attempts} tries`} on{' '}
            {formatDate(attempt.day)}
            {chaseReasonPhrase(attempt.reason) ? ` — ${chaseReasonPhrase(attempt.reason)}` : ''}.
            Nothing was sent, so #{inv.number} is
            still in the queue.
          </span>
        </span>
      </p>
      <Button className="mt-3 w-full" variant="outline" loading={run.isPending} onClick={retry}>
        <Send className="h-4 w-4" aria-hidden />
        Retry the chase now
      </Button>
    </div>
  );
}

function ChaseHoldCard({ inv }: { inv: InvoiceDetail }) {
  const setHold = useSetChaseHold();
  const [reason, setReason] = useState(inv.chase.holdReason ?? '');

  // The server is the authority on the reason; re-sync when it changes under us
  // (another tab, or the release-then-hold round trip).
  useEffect(() => {
    setReason(inv.chase.holdReason ?? '');
  }, [inv.chase.holdReason, inv.chase.held]);

  const held = inv.chase.held;
  const saveReason = () => {
    const next = reason.trim();
    if (!held || next === (inv.chase.holdReason ?? '')) return;
    setHold.mutate(
      { id: inv.id, held: true, reason: next || null },
      {
        onSuccess: () => toast.success(next ? 'Reason saved' : 'Reason cleared'),
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not save the reason'),
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title="Automatic chasing"
        subtitle={held ? 'Paused for this invoice only' : 'On the schedule with everything else'}
      />
      <CardBody>
        {held ? (
          <>
            <p className="flex items-start gap-2 text-body-sm">
              <PauseCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
              <span className="min-w-0">
                <span className="font-bold text-secondary">On hold.</span> The scheduler will not
                email this client about #{inv.number}.
                {inv.chase.holdBy && (
                  <span className="block text-caption text-grey-500">
                    Held by {inv.chase.holdBy}
                    {inv.chase.holdAt ? ` · ${formatDate(inv.chase.holdAt)}` : ''}
                  </span>
                )}
              </span>
            </p>
            {/* The reason is the thing nobody remembers in three weeks. Optional
                on the way in, editable here, never a gate on the decision. */}
            <Input
              className="mt-3"
              label="Why"
              placeholder="On a payment plan…"
              value={reason}
              maxLength={200}
              onChange={(e) => setReason(e.target.value)}
              onBlur={saveReason}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
            <p className="mt-3 text-caption text-grey-500">
              You can still send a reminder by hand — “Send reminder” above is unaffected.
            </p>
            <Button
              className="mt-3 w-full"
              variant="outline"
              loading={setHold.isPending}
              onClick={() =>
                setHold.mutate(
                  { id: inv.id, held: false },
                  {
                    onSuccess: () =>
                      toast.success(`#${inv.number} is back on the automatic chase queue`),
                    onError: (e) =>
                      toast.error(e instanceof Error ? e.message : 'Could not resume chasing'),
                  },
                )
              }
            >
              <PlayCircle className="h-4 w-4" aria-hidden />
              Resume automatic chasing
            </Button>
          </>
        ) : (
          <>
            <p className="text-body-sm text-grey-600">
              {inv.chase.nextFollowUp && inv.isLate
                ? `This invoice is in the automatic queue — chased every ${inv.chase.cadenceDays} days until it is paid.`
                : `Once it falls due it joins the automatic queue, chased every ${inv.chase.cadenceDays} days.`}
            </p>
            <Button
              className="mt-3 w-full"
              variant="outline"
              loading={setHold.isPending}
              onClick={() =>
                setHold.mutate(
                  { id: inv.id, held: true },
                  {
                    onSuccess: () =>
                      toast.success(`#${inv.number} taken off the automatic chase queue`),
                    onError: (e) =>
                      toast.error(e instanceof Error ? e.message : 'Could not hold the chase'),
                  },
                )
              }
            >
              <PauseCircle className="h-4 w-4" aria-hidden />
              Hold chasing on this invoice
            </Button>
            <AutomaticChaseFailure inv={inv} />
          </>
        )}
      </CardBody>
    </Card>
  );
}

/** Bill-to lines, in postal order, with the empty ones dropped. */
function billToLines(client: InvoiceDetail['client']): string[] {
  const cityLine = [client.city, [client.region, client.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [
    client.billingContactName,
    client.addressLine1,
    client.addressLine2,
    cityLine || null,
    client.country,
  ].filter((line): line is string => Boolean(line && line.trim()));
}

function hasBillingAddress(client: InvoiceDetail['client']): boolean {
  return billToLines(client).length > 0 || Boolean(client.billingEmail);
}

/**
 * WHO THIS CLIENT'S INVOICES ACTUALLY GO TO — all of it, on the record.
 *
 * ⚠ The audience was WRITE-ONLY. `delivery.service.ts#learn` saves every
 * address a send used onto the client, which is exactly what ended the manual
 * forward — and it only ever ADDS. So one address typed into the composer by
 * mistake silently joined every future invoice for that client, and the only
 * surface that could show it was the composer itself, which does not appear on
 * a PAID invoice. There was literally no screen in the app that could read the
 * list, let alone remove a name from it.
 *
 * It lives here because this is the block that already answers "where is this
 * invoice going", it is on every invoice in every state, and removing a chip
 * writes through the SAME `PATCH /invoices/:id/recipients` the composer uses —
 * one writer for the client's recipient columns, never a second.
 */
function BillingAudience({ invoice }: { invoice: InvoiceDetail }) {
  const save = useSetRecipients();
  const to = invoice.client.billingEmail;
  const cc = invoice.client.billingEmailCc ?? [];

  if (!to && cc.length === 0) return null;

  const remove = async (address: string) => {
    const nextTo = to && to !== address ? [to] : [];
    const nextCc = cc.filter((a) => a !== address);
    // Promoting the first copy to the addressee is what the server does with a
    // `to` of []; saying so beats leaving a client addressed to nobody.
    await save.mutateAsync({ id: invoice.id, to: nextTo, cc: nextCc });
    toast.success(`Removed ${address} from ${invoice.client.name}.`);
  };

  const row = (address: string, role: 'to' | 'cc') => (
    <li key={`${role}:${address}`} className="group flex min-w-0 items-center gap-1">
      <a
        href={`mailto:${address}`}
        className="min-w-0 truncate text-body-sm text-accent-700 hover:underline"
        title={address}
      >
        {address}
      </a>
      {/* The same word the composer's chips use — one vocabulary for one idea. */}
      {role === 'cc' && (
        <span className="shrink-0 text-caption uppercase tracking-wider text-grey-400">cc</span>
      )}
      <button
        type="button"
        onClick={() => remove(address)}
        disabled={save.isPending}
        aria-label={`Remove ${address} from ${invoice.client.name}`}
        title={`Remove ${address} — every future invoice for this client stops going to it`}
        className="ml-auto shrink-0 rounded p-0.5 text-grey-400 opacity-0 transition-opacity hover:bg-grey-100 hover:text-status-late focus:opacity-100 focus-visible:ring-2 focus-visible:ring-accent/40 group-hover:opacity-100 disabled:opacity-40"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </li>
  );

  return (
    <ul className="mt-1 space-y-0.5">
      {to ? row(to, 'to') : null}
      {cc.map((address) => row(address, 'cc'))}
      {cc.length > 0 && (
        <li className="pt-0.5 text-caption text-grey-500">
          {/* The count is the point: an invoice quietly reaching four people is
              the thing a reader needs to know before pressing Send. */}
          Every invoice for {invoice.client.name} goes to {cc.length + (to ? 1 : 0)} addresses.
        </li>
      )}
    </ul>
  );
}

/** " — releases 22 time entries and 2 expenses back to unbilled" (or ''). */
function releasedSummary(inv: Pick<InvoiceDetail, 'linkedTimeEntryCount' | 'linkedExpenseCount'>): string {
  const parts = [
    inv.linkedTimeEntryCount > 0 ? plural(inv.linkedTimeEntryCount, 'time entry', 'time entries') : null,
    inv.linkedExpenseCount > 0 ? plural(inv.linkedExpenseCount, 'expense', 'expenses') : null,
  ].filter(Boolean);
  return parts.length > 0 ? ` — releasing ${parts.join(' and ')} back to unbilled` : '';
}


// ---------------------------------------------------------------------------
// Billed work — the time entries and expenses behind the line items
// ---------------------------------------------------------------------------

function BilledWorkPanel({
  invoiceId,
  onDownloadDetail,
  downloading,
}: {
  invoiceId: string;
  /** Saves the invoice PDF with this breakdown appended. */
  onDownloadDetail: () => void;
  downloading: boolean;
}) {
  const work = useBilledWork(invoiceId, true);

  if (work.isLoading) {
    return (
      <div className="space-y-2 border-t border-grey-200 px-4 py-4 sm:px-6">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (work.isError || !work.data) {
    return (
      <p className="border-t border-grey-200 px-4 py-4 text-body-sm text-error sm:px-6">
        {work.error instanceof Error ? work.error.message : 'Could not load the billed time.'}
      </p>
    );
  }

  const { agreements, expenses, totals } = work.data;
  /**
   * ⚠ **AN INVOICE BUILT ON REWRITTEN HOURS HAS TO SAY SO, UP FRONT.**
   *
   * An approver may open anyone's draft week and change it. That is allowed,
   * and it is exactly the fact a client dispute turns on — so the count leads
   * the panel rather than waiting to be found as a chip on row 46 of 71.
   * Server-counted from the immutable trail; the browser infers nothing.
   */
  const rewritten = totals.editedByOthers;
  if (agreements.length === 0 && expenses.length === 0) {
    return (
      <p className="border-t border-grey-200 px-4 py-4 text-body-sm text-grey-500 sm:px-6">
        This invoice was written by hand — no time entries or expenses are linked to it.
      </p>
    );
  }

  return (
    <div className="border-t border-grey-200 bg-grey-50/60 px-4 py-4 sm:px-6">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        {/* A label, not a lesson: what the table IS stays; why we keep it and
            what it is for is behind the (i). */}
        <p className="flex flex-wrap items-center gap-x-1.5 text-caption text-grey-500">
          Every entry locked to this invoice, at the rate it was billed at.
          <InfoHint label="Billed work" width={310}>
            <p>
              The audit trail behind the line items above — every time entry and billable expense
              this invoice locked, at the rate it carried when it was billed.
            </p>
            <p className="mt-2">
              Clients ask for this constantly, so it can go out attached to the invoice.
            </p>
          </InfoHint>
        </p>
        <Button size="sm" variant="outline" onClick={onDownloadDetail} loading={downloading}>
          <Download className="h-4 w-4" aria-hidden />
          PDF with this detail
        </Button>
      </div>
      {rewritten > 0 && (
        <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-status-partial" aria-hidden />
          <p className="flex flex-wrap items-center gap-x-1.5 text-caption leading-snug text-grey-600">
            <span className="font-bold text-secondary">
              {rewritten === 1
                ? '1 of these entries was'
                : `${rewritten} of these ${totals.entryCount} entries were`}{' '}
              changed by somebody other than the person who logged the time.
            </span>
            <InfoHint label="Entries changed by somebody else" width={320}>
              <p>Marked in the table below with who and when.</p>
              <p className="mt-2">
                The full wording of each change is kept on the timesheet it happened to, and can
                never be edited or deleted.
              </p>
            </InfoHint>
          </p>
        </div>
      )}
      <div className="space-y-4">
        {agreements.map((p) => {
          /**
           * A column that prints the same figure 26 times in a row is a
           * sentence pretending to be data. When every entry on an agreement was
           * billed at one rate — which is the ordinary case — the rate is said
           * once, in the group's own header, and the column goes away.
           */
          const rates = new Set(p.entries.map((e) => e.rate));
          const oneRate = rates.size === 1 && p.entries.length > 1;
          return (
          <div key={p.agreementId} className="overflow-hidden rounded-lg border border-grey-200 bg-white">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-grey-200 px-3 py-2">
              <p className="font-semibold text-secondary">{p.agreementName}</p>
              <p className="num text-caption text-grey-500">
                {formatHours(p.hours, { unit: true })}
                {oneRate && ` · all at ${formatMoney([...rates][0])}/h`} ·{' '}
                {formatMoney(p.amount)}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="border-b border-grey-100 text-caption uppercase tracking-wider text-grey-500">
                    <th className="whitespace-nowrap px-2.5 py-1.5 text-left font-semibold">Date</th>
                    <th className="px-2.5 py-1.5 text-left font-semibold">Person</th>
                    <th className="hidden px-2.5 py-1.5 text-left font-semibold sm:table-cell">Task</th>
                    {/* Notes only where there is room — the money column must
                        never be pushed out of view (see CLAUDE.md). */}
                    <th className="hidden px-2.5 py-1.5 text-left font-semibold 2xl:table-cell">
                      Notes
                    </th>
                    <th className="px-2.5 py-1.5 text-right font-semibold">Hours</th>
                    {!oneRate && (
                      <th className="hidden px-2.5 py-1.5 text-right font-semibold sm:table-cell">
                        Rate
                      </th>
                    )}
                    <th className="px-2.5 py-1.5 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {p.entries.map((e) => (
                    <tr key={e.id} className="border-b border-grey-100 last:border-b-0">
                      <td className="num whitespace-nowrap px-2.5 py-1.5 text-grey-600">
                        {formatDate(e.date)}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 text-secondary">
                        {e.personName}
                        {/* Whose hour it is, then whose handwriting is on it.
                            Same chip as the timesheet's — one glyph per
                            concept, so a reader who learned it there is right
                            here. */}
                        {e.editedBy && (
                          <EditedByMark
                            editedBy={e.editedBy}
                            detail={e.editedBy.summary}
                            className="ml-1.5 align-middle"
                          />
                        )}
                      </td>
                      <td className="hidden whitespace-nowrap px-2.5 py-1.5 text-grey-600 sm:table-cell">
                        {e.taskName ?? '—'}
                      </td>
                      <td className="hidden max-w-[14rem] truncate px-2.5 py-1.5 text-grey-500 2xl:table-cell">
                        {e.notes ?? '—'}
                      </td>
                      {/* `fixed` — a right-aligned numeric column whose rows
                          read 4.75 / 7.75 / 4.5 / 6 / 3 has no decimal point
                          to line up on. */}
                      <td className="num-cell whitespace-nowrap px-2.5 py-1.5">
                        {formatHours(e.hours, { fixed: true })}
                      </td>
                      {!oneRate && (
                        <td className="num-cell hidden whitespace-nowrap px-2.5 py-1.5 text-grey-600 sm:table-cell">
                          {formatMoney(e.rate)}
                        </td>
                      )}
                      <td className="num-cell whitespace-nowrap px-2.5 py-1.5">
                        {formatMoney(e.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          );
        })}

        {expenses.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-grey-200 bg-white">
            <div className="border-b border-grey-200 px-3 py-2">
              <p className="font-semibold text-secondary">Billable expenses</p>
            </div>
            <table className="w-full text-body-sm">
              <tbody>
                {expenses.map((x) => (
                  <tr key={x.id} className="border-b border-grey-100 last:border-b-0">
                    <td className="num whitespace-nowrap px-3 py-1.5 text-grey-600">
                      {formatDate(x.date)}
                    </td>
                    <td className="px-3 py-1.5 text-secondary">{x.vendor ?? 'Expense'}</td>
                    <td className="hidden px-3 py-1.5 text-grey-500 sm:table-cell">
                      {x.agreementName ?? '—'}
                    </td>
                    <td className="num-cell px-3 py-1.5">{formatMoney(x.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="num mt-3 text-caption text-grey-500">
        {/* "h", never "hrs" — one unit per concept, app-wide. */}
        {totals.entryCount > 0 &&
          `${plural(totals.entryCount, 'time entry', 'time entries')} · ${formatHours(totals.hours, { unit: true })}`}
        {totals.entryCount > 0 && totals.expenseCount > 0 && ' · '}
        {totals.expenseCount > 0 && plural(totals.expenseCount, 'expense', 'expenses')}
        {' · '}
        {formatMoney(totals.amount)} of billed work
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Print-only view
//
// Renders `GET /api/invoices/:id/document` — the SAME object the server
// typesets into the downloadable PDF. There is one description of what an
// invoice says, and both surfaces read it, so the sheet on screen can never
// say something different from the file the client receives.
// ---------------------------------------------------------------------------

/**
 * The client-facing sheet itself. One component, three surfaces: the print
 * portal below, the on-screen Preview, and — through the same `document`
 * payload — the server-typeset PDF. Nothing here may say something the file
 * the client receives does not.
 */
function InvoicePrintView({ doc }: { doc: InvoiceDocument }) {
  // Portaled to <body> so print CSS can hide the whole app (#root) and show
  // only this clean invoice sheet.
  return createPortal(
    <div className="invoice-print-root hidden print:block">
      <style>{`
        @media print {
          body { background: #fff !important; }
          #root { display: none !important; }
          .invoice-print-root {
            display: block !important;
            width: 100%; padding: 1rem 0; background: #fff; color: #142033;
          }
          /* Repeat the line-item head on every printed page and never split a
             row — the same rule the PDF paginator follows. */
          .invoice-print-root thead { display: table-header-group; }
          .invoice-print-root tr { break-inside: avoid; }
          .invoice-print-totals, .invoice-print-remit { break-inside: avoid; }
          @page { margin: 14mm; }
        }
      `}</style>
      <InvoiceSheet doc={doc} />
    </div>,
    document.body,
  );
}

/**
 * "Show me what the client sees" — on screen, before it goes out, without a
 * download or a print dialog. It renders the SAME document object the PDF is
 * typeset from, so the preview cannot flatter the file.
 */
function InvoicePreviewModal({
  doc,
  open,
  onOpenChange,
  onDownloadPdf,
  downloading,
  onPrint,
}: {
  doc: InvoiceDocument;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownloadPdf: () => void;
  downloading: boolean;
  onPrint: () => void;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      className="max-h-[92vh] max-w-4xl"
      title={`Invoice #${doc.number} — as the client sees it`}
      description="The same document the PDF is typeset from, so this page and the file cannot disagree."
      footer={
        <div className="flex w-full flex-col gap-2 border-t border-grey-100 pt-4 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onPrint}>
            <Printer className="h-4 w-4" aria-hidden />
            Print
          </Button>
          <Button onClick={onDownloadPdf} loading={downloading}>
            <Download className="h-4 w-4" aria-hidden />
            Download PDF
          </Button>
        </div>
      }
    >
      {/**
       * SCALED TO FIT, not scrolled.
       *
       * At full size this sheet is taller than a laptop viewport, so the one
       * screen whose entire job is answering "does this look client-ready?"
       * showed the letterhead, Bill To and the subject line — and nothing
       * else. `zoom` rather than `transform: scale` on purpose: scale leaves
       * the element's original height in the flow, so the dialog would end
       * with a third of a page of white nothing under the sheet.
       */}
      <div className="-mx-1 max-h-[68vh] overflow-y-auto overflow-x-hidden px-1">
        <div style={{ zoom: 0.72 }}>
          <div className="rounded-lg border border-grey-200 bg-white p-6 shadow-sm">
            <InvoiceSheet doc={doc} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Detail page
// ---------------------------------------------------------------------------

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invoice = useInvoice(id);
  const send = useSendInvoice();
  const voidInvoice = useVoidInvoice();
  const deleteInvoice = useDeleteInvoice();
  const removePayment = useRemovePayment();
  const logReminder = useLogReminder();
  const removeReminder = useRemoveReminder();
  const [paymentOpen, setPaymentOpen] = useState(false);
  // One composer, two jobs. Null = closed; the mode decides whether committing
  // issues the invoice or chases it.
  const [composer, setComposer] = useState<ComposerMode | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [showBilledWork, setShowBilledWork] = useState(false);
  const [confirmVoid, setConfirmVoid] = useState(false);
  const [confirmReceipt, setConfirmReceipt] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [paymentToRemove, setPaymentToRemove] = useState<InvoicePayment | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const duplicate = useDuplicateInvoice();
  const [downloading, setDownloading] = useState<'plain' | 'detail' | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // The client-facing document: the print sheet renders it, and the server
  // typesets the same object into the PDF the Download button streams.
  const printDoc = useInvoiceDocument(id);
  const [searchParams, setSearchParams] = useSearchParams();
  // Where the reader came from. `client` means this invoice was opened from a
  // client's Invoices tab, so every way out of this page — Back, the sibling
  // invoices, the draft editor, the redirect after a delete — stays inside
  // that client instead of dropping the reader into the whole portfolio.
  const from = useReturnOrigin();

  const savePdf = async (options: { detail?: boolean; quiet?: boolean } = {}) => {
    if (!id) return;
    setDownloading(options.detail ? 'detail' : 'plain');
    try {
      const fileName = await downloadInvoicePdf(id, { detail: options.detail });
      if (!options.quiet) toast.success(`${fileName} downloaded`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not build the PDF');
    } finally {
      setDownloading(null);
    }
  };

  // ?remind=1 / ?send=1 — the list's row-level actions hand off to here, where
  // the client's billing contact and the remittance block are already loaded.
  // The list can open the composer but never commits a delivery blind.
  useEffect(() => {
    const wants: ComposerMode | null =
      searchParams.get('send') === '1'
        ? 'send'
        : searchParams.get('remind') === '1'
          ? 'remind'
          : null;
    if (!wants) return;
    setComposer(wants);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('remind');
        next.delete('send');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const lineColumns: Array<DataTableColumn<InvoiceLineItem>> = [
    {
      key: 'description',
      header: 'Description',
      render: (li) => (
        <div>
          <p className="font-medium text-secondary">{displayHoursUnit(li.description)}</p>
          <p className="text-caption text-grey-500">
            <span className="capitalize">{li.kind}</span>
            {' · '}
            {/* Which agreement this line billed — and a way to change it, on any
                invoice: MxD's VTC hours were billed on the right invoice against
                an agreement nobody logs time to. */}
            {invoice.data ? (
              <LineAgreementLink invoiceId={invoice.data.id} clientId={invoice.data.client.id} line={li} />
            ) : null}
            {invoice.data && li.prepaid && (li.prepaid.canAdd || li.prepaid.bankedHours !== null) ? (
              <>
                {' · '}
                <LineBankAction
                  invoiceId={invoice.data.id}
                  invoiceNumber={invoice.data.number}
                  invoiceSent={invoice.data.state !== 'DRAFT'}
                  clientId={invoice.data.client.id}
                  line={li}
                />
              </>
            ) : null}
          </p>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      render: (li) => li.quantity,
      className: 'w-20',
    },
    {
      key: 'unitPrice',
      header: 'Unit price',
      align: 'right',
      render: (li) => formatMoney(li.unitPrice),
      className: 'w-32 hidden sm:table-cell',
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (li) => formatMoney(li.amount),
      className: 'w-32',
    },
  ];

  const paymentColumns: Array<DataTableColumn<InvoicePayment>> = [
    {
      key: 'date',
      header: 'Date',
      render: (p) => <span className="num whitespace-nowrap">{formatDate(p.date)}</span>,
    },
    {
      key: 'method',
      header: 'Method',
      render: (p) => (p.method ? METHOD_LABELS[p.method] ?? p.method : '—'),
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (p) => <span className="text-grey-600">{p.notes ?? '—'}</span>,
      className: 'hidden md:table-cell',
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (p) => formatMoney(p.amount),
    },
    ...(invoice.data && invoice.data.state !== 'VOID'
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            className: 'w-11 px-2',
            render: (p: InvoicePayment) => (
              <button
                type="button"
                aria-label={`Remove ${formatMoney(p.amount)} payment`}
                title="Remove payment"
                onClick={() => setPaymentToRemove(p)}
                className="rounded-lg p-1.5 text-grey-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            ),
          },
        ]
      : []),
  ];

  if (invoice.isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl">
        <Skeleton className="mb-2 h-5 w-32" />
        <Skeleton className="mb-6 h-9 w-64" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
          <Skeleton className="h-72 w-full" />
        </div>
      </div>
    );
  }

  if (invoice.isError || !invoice.data) {
    // A missing invoice and a broken server are different problems with
    // different fixes: "not found" sends you to the org switcher, a 500 wants
    // Try again. Announcing a server fault as "Invoice not found" sends people
    // hunting for a record that exists.
    const status = invoice.error instanceof ApiError ? invoice.error.status : 0;
    const missing = status === 404 || status === 403;
    // A raw server message is evidence, not an explanation: a 500 whose body
    // reads "boom" must not be the sentence the user is handed.
    const problem = missing
      ? {
          title: 'Invoice not found',
          description:
            'No invoice with this id in the current organization. It may have been deleted, or it belongs to another organization — check the org switcher.',
          detail: null,
        }
      : describeError(invoice.error, 'this invoice');
    return (
      <div className="mx-auto w-full max-w-5xl">
        <Card>
          <EmptyState
            icon={missing ? <FileText /> : <AlertTriangle />}
            title={problem.title}
            description={problem.description}
            action={
              <div className="flex flex-col items-center gap-3">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {!missing && (
                    <Button onClick={() => void invoice.refetch()} loading={invoice.isFetching}>
                      Try again
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => navigate(invoiceListHref())}>
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    Back to invoices
                  </Button>
                </div>
                {problem.detail && (
                  <p className="num text-caption text-grey-400">Server said: {problem.detail}</p>
                )}
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  const inv = invoice.data;
  const canPay = inv.state === 'OPEN' || inv.state === 'PARTIAL';
  const canVoid = canPay || inv.state === 'DRAFT';
  const hasBilledWork = inv.linkedTimeEntryCount > 0 || inv.linkedExpenseCount > 0;
  const billedWorkLabel = [
    inv.linkedTimeEntryCount > 0
      ? plural(inv.linkedTimeEntryCount, 'time entry', 'time entries')
      : null,
    inv.linkedExpenseCount > 0 ? plural(inv.linkedExpenseCount, 'expense', 'expenses') : null,
  ]
    .filter(Boolean)
    .join(' + ');
  const billTo = billToLines(inv.client);
  // "Chased 3× · last 3d ago" — null when there is nothing to say.
  const chaseLine = chaseLabel(inv.chase, inv.isLate);

  /**
   * The delivery went somewhere other than the billing address on file — the
   * one delivery fact the header chip is too short to raise, and the reason
   * "we never received it" sometimes has an answer nobody expects.
   */
  const sentElsewhere =
    inv.sentRecord?.to &&
    inv.client.billingEmail &&
    inv.sentRecord.to.trim().toLowerCase() !== inv.client.billingEmail.trim().toLowerCase()
      ? inv.sentRecord.to
      : null;
  /** Is there anything in the delivery record the chip and Activity do not say? */
  const deliveryExtras = Boolean(
    inv.sentRecord && (inv.sentRecord.subject || inv.sentRecord.note || sentElsewhere),
  );

  /**
   * Activity is built from things that HAPPENED to the invoice — issued, sent,
   * paid, chased, voided — dated by the event, never by the database row.
   *
   * `createdAt` and `updatedAt` are row timestamps: an import stamps every one
   * of them with the day the import ran, and a bulk write stamps `updatedAt`
   * with the day it ran. Printed as history they invent one — "Draft created
   * Aug 12, 2026" above "Sent Nov 4, 2025" is a sequence that could not have
   * occurred, and it is the first thing a reader disbelieves on a page whose
   * whole job is to be believed. So:
   *
   * - the first row of a sent invoice is **Issued**, dated `issueDate`;
   * - "Draft created" appears only when `createdAt` is not after the issue date
   *   (i.e. the invoice really was drafted here first) or while it is still a
   *   draft, where the creation IS the only event so far;
   * - "Voided" carries no date, because we do not record when a void happened —
   *   `updatedAt` is merely the last time anything touched the row. An honest
   *   blank beats a confident wrong date.
   */
  const createdAtDay = inv.createdAt.slice(0, 10);
  const showCreated = inv.state === 'DRAFT' || createdAtDay <= inv.issueDate;
  const issued = inv.state !== 'DRAFT';
  const activity: Array<{
    key: string;
    /** Sort key only — `meta` is what the row prints. */
    date: string;
    rank: number;
    dot: string;
    title: string;
    meta: string;
  }> = [
    ...(showCreated
      ? [
          {
            key: 'created',
            date: createdAtDay,
            rank: 0,
            dot: 'bg-grey-300',
            title: 'Draft created',
            meta: formatDate(inv.createdAt),
          },
        ]
      : []),
    ...(issued
      ? [
          {
            key: 'issued',
            date: inv.issueDate,
            rank: 1,
            dot: 'bg-status-open',
            title: 'Issued',
            meta: `${formatDate(inv.issueDate)} · due ${formatDate(inv.dueDate)}`,
          },
          // The delivery that started the clock — with the address on it. An
          // "issued on the 3rd" that cannot say who received it is the row a
          // disputed invoice needs most.
          inv.sentRecord
            ? {
                key: 'sent',
                date: inv.sentRecord.at.slice(0, 10),
                rank: 2,
                dot: 'bg-status-open',
                title: `Sent${inv.sentRecord.to ? ` · ${inv.sentRecord.to}` : ''}`,
                meta: [
                  formatDate(inv.sentRecord.at),
                  CHANNEL_LABELS[inv.sentRecord.channel] ?? inv.sentRecord.channel,
                  inv.sentRecord.note,
                ]
                  .filter(Boolean)
                  .join(' · '),
              }
            : {
                key: 'sent',
                date: inv.issueDate,
                rank: 2,
                dot: 'bg-grey-300',
                title: 'Marked as sent',
                meta: 'No delivery recorded',
              },
        ]
      : []),
    ...inv.payments.map((p) => ({
      key: `pay-${p.id}`,
      date: p.date,
      rank: 3,
      dot: 'bg-status-paid',
      title: `Payment received · ${formatMoney(p.amount)}`,
      meta: `${formatDate(p.date)}${p.method ? ` · ${METHOD_LABELS[p.method] ?? p.method}` : ''}`,
    })),
    // The corrected invoice, sent again — an invoice send, not a chase.
    ...(inv.resends ?? []).map((r) => ({
      key: `resend-${r.id}`,
      date: r.sentAt.slice(0, 10),
      rank: 3,
      dot: 'bg-status-open',
      title: `Updated invoice sent${r.to ? ` · ${r.to}` : ''}`,
      meta: `${formatDate(r.sentAt)} · ${CHANNEL_LABELS[r.channel] ?? r.channel}${
        r.sentBy ? ` · by ${r.sentBy}` : ''
      }${r.note ? ` · ${r.note}` : ''}`,
    })),
    // Every chase, in the same timeline as the money — "reminder sent, then
    // nothing for 19 days" is the story this card exists to tell.
    ...inv.reminders.map((r) => ({
      key: `remind-${r.id}`,
      date: r.sentAt.slice(0, 10),
      rank: 3,
      dot: 'bg-warning',
      // An automatic chase says so in the TITLE, not buried at the end of a
      // note the reader has to finish to learn who acted. "Nothing happened
      // here unless a human did it" was the whole defect; the timeline is where
      // that is disproved.
      title: `${r.auto ? 'Reminder sent automatically' : 'Reminder sent'}${r.to ? ` · ${r.to}` : ''}`,
      meta: `${formatDate(r.sentAt)} · ${CHANNEL_LABELS[r.channel] ?? r.channel}${
        r.note ? ` · ${r.note}` : ''
      }`,
    })),
    ...(inv.state === 'VOID'
      ? [
          {
            // Terminal by definition, so it sorts to the top without claiming a
            // day we never recorded.
            key: 'void',
            date: '9999-12-31',
            rank: 4,
            dot: 'bg-status-void',
            title: 'Voided',
            meta: 'Billed time released back to unbilled',
          },
        ]
      : []),
  ].sort((a, b) => b.date.localeCompare(a.date) || b.rank - a.rank);


  return (
    <div className="mx-auto w-full max-w-5xl">
      <BackLink
        to={from === 'client' ? `/clients/${inv.client.id}?tab=invoices` : invoiceListHref()}
        className="mb-3"
      >
        {from === 'client' ? inv.client.name : 'Invoices'}
      </BackLink>

      <PageHeader
        title={`Invoice #${inv.number}`}
        subtitle={
          <span className="block">
            {inv.subject && (
              <span className="mb-1 block font-semibold text-secondary">{inv.subject}</span>
            )}
            <span className="flex flex-wrap items-center gap-2">
              <StatusPill
                status={invoiceStatusToPill(inv.state, inv.isLate)}
                // A late invoice that has taken a partial payment says both.
                label={inv.isLate && inv.state === 'PARTIAL' ? 'LATE · PARTIAL' : undefined}
              />
              <span>
                {inv.client.name} · issued {formatDate(inv.issueDate)}
                {inv.isLate && (
                  <span className="font-semibold text-status-late">
                    {' '}
                    · {inv.daysOverdue} days overdue
                  </span>
                )}
                {/* Most PO numbers already start with "PO" — don't say it twice. */}
                {inv.poNumber && (
                  <span className="num">
                    {' · '}
                    {/^po\b/i.test(inv.poNumber) ? inv.poNumber : `PO ${inv.poNumber}`}
                  </span>
                )}
              </span>
              {/* Did it ever actually go out, and to whom — the first question
                  a disputed invoice raises, answered in the header. */}
              {inv.sentRecord && (
                <span
                  className="num inline-flex max-w-full items-center gap-1.5 truncate rounded-full bg-status-open-bg px-2 py-0.5 text-caption font-semibold text-status-open"
                  title={`${CHANNEL_LABELS[inv.sentRecord.channel] ?? inv.sentRecord.channel} · ${formatDate(
                    inv.sentRecord.at,
                  )}${inv.sentRecord.subject ? ` · ${inv.sentRecord.subject}` : ''}`}
                >
                  <MailCheck className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="truncate">{sentLabel(inv.sentRecord)}</span>
                </span>
              )}
              {/* Whether this debt is being worked, before you open the
                  composer: "Chased 3× · last 3d ago" or a red "Never chased". */}
              {chaseLine && (
                <span
                  className={cn(
                    'num inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-caption font-semibold',
                    inv.chase.count === 0
                      ? 'bg-status-late-bg text-status-late'
                      : inv.chase.dueForFollowUp
                        ? 'bg-warning/15 text-secondary'
                        : 'bg-grey-100 text-grey-600',
                  )}
                  title={
                    inv.chase.nextFollowUp
                      ? `${followUpLabel(inv.chase, inv.isLate)} — chase cadence is every ${inv.chase.cadenceDays} days`
                      : undefined
                  }
                >
                  <BellRing className="h-3 w-3" aria-hidden />
                  {chaseLine}
                  {inv.isLate && followUpLabel(inv.chase, inv.isLate) && (
                    <span className="font-normal opacity-80">
                      · {followUpLabel(inv.chase, inv.isLate)}
                    </span>
                  )}
                </span>
              )}
            </span>
          </span>
        }
        actions={
          // Five actions wrapped into a ragged 2-2-1 on a phone. A two-column
          // grid with the primary action spanning both gives 1-2-2 instead:
          // even rows, and the thing you came to do is full width.
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
            {inv.state === 'DRAFT' && (
              <>
                {/* Sending opens the composer, not a bare state flip: the
                    delivery that starts the payment clock is recorded the same
                    way every chase after it is. */}
                <Button
                  className="col-span-2 sm:col-auto"
                  onClick={() => setComposer('send')}
                  loading={send.isPending}
                >
                  <Send className="h-4 w-4" aria-hidden />
                  Send invoice
                </Button>
                <Button className="col-span-2 sm:col-auto" variant="outline" onClick={() => setScheduling(true)}>
                  <CalendarClock className="h-4 w-4" aria-hidden />
                  {inv.scheduledSend ? 'Reschedule' : 'Schedule send'}
                </Button>
                <Button variant="outline" onClick={() => navigate(withReturn(`/invoices/${inv.id}/edit`, from))}>
                  <PencilLine className="h-4 w-4" aria-hidden />
                  Edit
                </Button>
                <Button variant="outline" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                  Delete
                </Button>
              </>
            )}
            {canPay && (
              <Button className="col-span-2 sm:col-auto" onClick={() => setPaymentOpen(true)}>
                <CreditCard className="h-4 w-4" aria-hidden />
                Record payment
              </Button>
            )}
            {/* A courtesy nudge before the due date is the most valuable
                reminder there is — not just the chase after it. */}
            {canPay && (
              <Button variant="outline" onClick={() => setComposer('resend')}>
                <Send className="h-4 w-4" aria-hidden />
                Re-send invoice
              </Button>
            )}
            {canPay && (
              <Button variant="outline" onClick={() => setComposer('remind')}>
                <BellRing
                  className={cn('h-4 w-4', inv.isLate && 'text-status-late')}
                  aria-hidden
                />
                Send reminder
              </Button>
            )}
            {/* ONE button for the document.
                Preview, Download PDF and Print were three equal-weight
                top-level actions producing the SAME artifact — three choices
                where there is one decision ("show me the invoice"). Download
                and Print now live inside the preview, next to the thing they
                act on. */}
            <Button
              variant="outline"
              disabled={!printDoc.data}
              title={
                printDoc.data
                  ? 'Show the invoice exactly as the client receives it — download or print it from there'
                  : 'Preparing the invoice sheet…'
              }
              onClick={() => setPreviewOpen(true)}
            >
              <Eye className="h-4 w-4" aria-hidden />
              View invoice
            </Button>
            <Button
              variant="outline"
              loading={duplicate.isPending}
              title="A new draft for the same client with the same lines and terms, dated today"
              onClick={() => {
                setActionError(null);
                duplicate
                  .mutateAsync(inv.id)
                  .then((copy) => {
                    toast.success(
                      `Draft #${copy.number} created from #${copy.duplicatedFrom}, dated today` +
                        (copy.soldHours
                          ? ' — it sold prepaid hours; add them to the bank from its line once you have checked it'
                          : ''),
                    );
                    navigate(withReturn(`/invoices/${copy.id}/edit`, from));
                  })
                  .catch((e: unknown) => setActionError(e instanceof Error ? e.message : 'Could not duplicate this invoice.'));
              }}
            >
              <Copy className="h-4 w-4" aria-hidden />
              Duplicate
            </Button>
            {/* A paid invoice's receipt, again — #26-0015's Stripe charge
                landed before the client had a billing email, so the
                automatic one was skipped (Jesse, 2026-10-01). */}
            {inv.state === 'PAID' && (
              <Button variant="outline" onClick={() => setConfirmReceipt(true)}>
                <Send className="h-4 w-4" aria-hidden />
                Re-send receipt
              </Button>
            )}
            {canVoid && inv.state !== 'DRAFT' && (
              <Button variant="outline" onClick={() => setConfirmVoid(true)}>
                <Ban className="h-4 w-4" aria-hidden />
                Void
              </Button>
            )}
          </div>
        }
      />

      <ScheduledSendNotice invoice={inv} onChange={() => setScheduling(true)} />

      {actionError && (
        <div className="mb-4 rounded-lg border border-status-late-border bg-status-late-bg px-4 py-3 text-body-sm font-medium text-status-late">
          {actionError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Line items"
              subtitle={hasBilledWork ? `${billedWorkLabel} billed on this invoice` : undefined}
              actions={
                <div className="flex flex-wrap items-center gap-2">
                {(inv.state === 'DRAFT' || inv.state === 'OPEN' || inv.state === 'PARTIAL') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(withReturn(`/invoices/${inv.id}/edit`, from))}
                  >
                    <PencilLine className="h-3.5 w-3.5" aria-hidden />
                    Edit
                  </Button>
                )}
                {hasBilledWork ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-expanded={showBilledWork}
                    onClick={() => setShowBilledWork((v) => !v)}
                  >
                    {showBilledWork ? (
                      <ChevronDown className="h-4 w-4" aria-hidden />
                    ) : (
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    )}
                    {showBilledWork ? 'Hide the detail' : 'Show the detail'}
                  </Button>
                ) : null}
                </div>
              }
            />
            <DataTable
              columns={lineColumns}
              data={inv.lineItems}
              rowKey={(li) => li.id}
              empty={
                <EmptyState icon={<FileText />} title="No line items" description="This invoice is empty." />
              }
            />
            {hasBilledWork && showBilledWork && (
              <BilledWorkPanel
                invoiceId={inv.id}
                onDownloadDetail={() => void savePdf({ detail: true })}
                downloading={downloading === 'detail'}
              />
            )}
            {/* Totals */}
            <div className="flex justify-end border-t border-grey-200 px-4 py-4 sm:px-6">
              <dl className="num w-full max-w-xs space-y-1.5 text-body-sm">
                <div className="flex justify-between">
                  <dt className="text-grey-500">Subtotal</dt>
                  <dd className="font-medium">{formatMoney(inv.subtotal)}</dd>
                </div>
                {inv.discountAmount > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-grey-500">Discount</dt>
                    {/* Money coming off reads the way it reads everywhere else
                        in this app: parenthesised, in green. */}
                    <dd className="font-medium text-success">
                      ({formatMoney(inv.discountAmount)})
                    </dd>
                  </div>
                )}
                {inv.taxAmount > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-grey-500">Tax</dt>
                    <dd className="font-medium">{formatMoney(inv.taxAmount)}</dd>
                  </div>
                )}
                <div className="flex justify-between border-t border-grey-200 pt-1.5 text-base">
                  <dt className="font-bold text-secondary">Total</dt>
                  <dd className="font-bold text-secondary">{formatMoney(inv.amount)}</dd>
                </div>
                {inv.paidAmount > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-grey-500">Paid</dt>
                    <dd className="font-medium text-status-paid">({formatMoney(inv.paidAmount)})</dd>
                  </div>
                )}
                <div className="flex justify-between border-t-2 border-secondary pt-1.5 text-base">
                  <dt className="font-black text-secondary">Amount due</dt>
                  <dd className={cn('font-black', inv.isLate ? 'text-status-late' : 'text-secondary')}>
                    {formatMoney(inv.dueAmount)}
                  </dd>
                </div>
              </dl>
            </div>
          </Card>

          <Card id="invoice-payments">
            <CardHeader
              title="Payments"
              subtitle={
                inv.payments.length > 0
                  ? `${formatMoney(inv.paidAmount)} of ${formatMoney(inv.amount)} received`
                  : undefined
              }
              // One Record payment button per screen. The page header already
              // carries the primary; a card header, an empty state and a header
              // all offering the same verb is three buttons pretending to be
              // three decisions.
              actions={
                canPay && inv.payments.length > 0 ? (
                  <Button size="sm" variant="outline" onClick={() => setPaymentOpen(true)}>
                    <CreditCard className="h-4 w-4" aria-hidden />
                    Record payment
                  </Button>
                ) : undefined
              }
            />
            <DataTable
              columns={paymentColumns}
              data={inv.payments}
              rowKey={(p) => p.id}
              empty={
                // One sentence does not need a 400px card. A single line, in
                // the row where the first payment will appear.
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-grey-100 px-4 py-3 text-body-sm text-grey-500 sm:px-6">
                  <CreditCard className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
                  {canPay ? (
                    <>
                      <span>Nothing received yet — partial payments are fine.</span>
                      <button
                        type="button"
                        onClick={() => setPaymentOpen(true)}
                        className="font-semibold text-accent-700 underline-offset-2 hover:underline"
                      >
                        Record payment
                      </button>
                    </>
                  ) : inv.state === 'DRAFT' ? (
                    <span>Send the invoice first — payments land here once it is open.</span>
                  ) : (
                    <span>This invoice has no recorded payments.</span>
                  )}
                </p>
              }
            />
          </Card>

          {/* Activity lives in the main column: the right rail is ~350px wide,
              which wraps every "Reminder sent · vendorpayments@harbormain.example"
              onto two lines while this column sat empty below the payments
              card. The timeline is the widest thing on the page — give it the
              width. */}
          <Card>
            <CardHeader
              title="Activity"
              subtitle={`${plural(activity.length, 'event', 'events')} on this invoice, newest first`}
            />
            <CardBody>
              <ul className="space-y-3 text-body-sm">
                {activity.map((event) => (
                  <li key={event.key} className="flex gap-3">
                    <span
                      className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', event.dot)}
                      aria-hidden
                    />
                    <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3">
                      <p className="min-w-0 break-words font-medium text-secondary">{event.title}</p>
                      <p className="num text-caption text-grey-500">{event.meta}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </div>

        {/* Side column */}
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader
              title="Bill to"
              actions={
                <Link
                  to={`/clients/${inv.client.id}`}
                  className="text-body-sm font-semibold text-accent-700 hover:underline"
                >
                  {hasBillingAddress(inv.client) ? 'Edit' : 'Add address'}
                </Link>
              }
            />
            <CardBody>
              <p className="text-base font-bold text-secondary">{inv.client.name}</p>
              {/* The exact block the printed invoice carries — if it is empty
                  here, the invoice you hand a client is unaddressed. */}
              {billTo.map((line) => (
                <p key={line} className="text-body-sm text-grey-600">
                  {line}
                </p>
              ))}
              {inv.client.taxId && (
                <p className="num mt-0.5 text-body-sm text-grey-500">{inv.client.taxId}</p>
              )}
              {/* ⚠ THE AUDIENCE WAS WRITE-ONLY. Every send learns the addresses
                  it used onto the client (that is what ended the manual
                  forward), and `learn` only ever ADDS — so one fat-fingered
                  address typed into the composer once joined every future
                  invoice for that client, and the only screen that could show
                  it was the composer, which does not exist on a PAID invoice.
                  The whole list lives here now, where the bill-to block already
                  is, and each address can be taken off. */}
              <BillingAudience invoice={inv} />
              {!hasBillingAddress(inv.client) && (
                <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-warning/10 px-2.5 py-2 text-caption text-secondary">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
                  <span>
                    No billing address — the printed invoice will show only the company name.
                  </span>
                </p>
              )}
              {/* What this client owes ALTOGETHER.
                  Looking at #1156 for $21,207.50 while the same client's #1144
                  sits late-partial at $7,825 is how you chase Halcyon twice
                  instead of once. The invoice cannot see that from its own row,
                  so the payload carries the rest of the client's book. */}
              {inv.clientBalance.otherCount > 0 && (
                <div className="mt-3 rounded-lg border border-grey-200 bg-grey-50 px-2.5 py-2">
                  <p className="text-body-sm font-semibold text-secondary">
                    {inv.client.name} owes{' '}
                    <span className="num">
                      {formatMoney(inv.dueAmount + inv.clientBalance.otherDue, { whole: true })}
                    </span>{' '}
                    across {inv.clientBalance.otherCount + 1} invoices
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {inv.clientBalance.others.slice(0, 4).map((o) => (
                      <li key={o.id} className="num flex justify-between gap-2 text-caption">
                        <Link
                          to={withReturn(`/invoices/${o.id}`, from)}
                          className="font-semibold text-accent-700 hover:underline"
                        >
                          #{o.number}
                        </Link>
                        <span className={cn(o.isLate ? 'text-status-late' : 'text-grey-500')}>
                          {formatMoney(o.dueAmount)}
                          {o.isLate ? ` · ${o.daysOverdue}d late` : ` · due ${formatDate(o.dueDate)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {inv.clientBalance.otherCount > 4 && (
                    <Link
                      to={`/invoices?client=${inv.client.id}`}
                      className="mt-1 block text-caption font-semibold text-accent-700 hover:underline"
                    >
                      See all {inv.clientBalance.otherCount + 1}
                    </Link>
                  )}
                </div>
              )}
              {inv.client.notes && <p className="mt-2 text-body-sm text-grey-500">{inv.client.notes}</p>}
              <dl className="num mt-4 space-y-2 border-t border-grey-100 pt-4 text-body-sm">
                <div className="flex justify-between">
                  <dt className="text-grey-500">Kind</dt>
                  <dd className="font-medium capitalize">{inv.kind}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-grey-500">Issue date</dt>
                  <dd className="font-medium">{formatDate(inv.issueDate)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-grey-500">Due date</dt>
                  <dd className={cn('font-medium', inv.isLate && 'font-bold text-status-late')}>
                    {formatDate(inv.dueDate)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-grey-500">Terms</dt>
                  <dd className="font-medium">{invoiceTerms(inv.issueDate, inv.dueDate)}</dd>
                </div>
                {inv.paidDate && (
                  <div className="flex justify-between">
                    <dt className="text-grey-500">Paid on</dt>
                    <dd className="font-medium text-status-paid">{formatDate(inv.paidDate)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-grey-500">Currency</dt>
                  <dd className="font-medium">{inv.currency}</dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/*
            ⚠ **THIS CARD ONLY SAYS WHAT THE OTHER TWO CANNOT.**
            The delivery fact used to be stated THREE times on one screen: the
            header chip ("Sent to ap@… · 7d ago"), the Activity row ("Sent ·
            ap@… · Aug 6, 2026 · Email") and a full card here repeating the
            address, the date, the channel and the age — which on a phone cost
            most of a screen of scrolling for one fact already read twice.

            What survives is the part nothing else carries: the SUBJECT LINE
            actually sent, the internal note, and — the one thing that would be
            genuinely alarming to miss — a delivery that went somewhere other
            than the billing address on file. When there is none of that, the
            card does not render at all, because the chip and the timeline have
            already answered it. The "no delivery record" state is not
            redundancy and always renders.
          */}
          {inv.state !== 'DRAFT' && (!inv.sentRecord || deliveryExtras) && (
            <Card>
              <CardHeader
                title={inv.sentRecord ? 'What we sent' : 'Delivery'}
                subtitle={
                  inv.sentRecord ? 'The message the client received' : undefined
                }
              />
              <CardBody>
                {inv.sentRecord ? (
                  <>
                    {sentElsewhere && (
                      <p className="flex items-start gap-2 text-body-sm">
                        <AlertTriangle
                          className="mt-0.5 h-4 w-4 shrink-0 text-warning"
                          aria-hidden
                        />
                        <span className="min-w-0">
                          Delivered to{' '}
                          <a
                            href={`mailto:${sentElsewhere}`}
                            className="break-all font-semibold text-accent-700 hover:underline"
                          >
                            {sentElsewhere}
                          </a>
                          <span className="block text-caption text-grey-500">
                            Not the billing address on file
                            {inv.client.billingEmail ? ` (${inv.client.billingEmail})` : ''}.
                          </span>
                        </span>
                      </p>
                    )}
                    {inv.sentRecord.subject && (
                      <p
                        className={cn(
                          'border-l-2 border-grey-200 pl-2.5 text-body-sm italic text-grey-600',
                          sentElsewhere && 'mt-2',
                        )}
                      >
                        “{inv.sentRecord.subject}”
                      </p>
                    )}
                    {inv.sentRecord.note && (
                      <p className="mt-2 text-caption text-grey-500">{inv.sentRecord.note}</p>
                    )}
                  </>
                ) : (
                  <p className="flex items-start gap-2 text-body-sm text-grey-600">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-grey-400" aria-hidden />
                    <span>
                      No delivery record. This invoice was issued before sends were logged (or
                      migrated in from Harvest, which has none to import), so we cannot say who
                      received it — only that it was issued {formatDate(inv.issueDate)}.
                    </span>
                  </p>
                )}
              </CardBody>
            </Card>
          )}

          {/* Only where it can actually do something: a DRAFT is never chased
              (the scheduler's queue is late-only by construction) and a PAID or
              VOID invoice has nothing left to chase. A control that renders
              everywhere and works somewhere is worse than one that appears when
              it applies. */}
          {(inv.state === 'OPEN' || inv.state === 'PARTIAL') && <ChaseHoldCard inv={inv} />}

          {/* The invoice note is CLIENT-FACING — it is printed on the PDF under
              "Notes on this invoice". Sitting unlabelled inside the Activity
              card it read as an internal memo, which is how "Disputed line item
              — remainder on hold" ends up on a client's invoice. */}
          {inv.notes && (
            <Card>
              <CardHeader
                title="Note on the invoice"
                subtitle="Printed on the PDF the client receives"
                actions={
                  inv.state === 'DRAFT' ? (
                    <Link
                      to={withReturn(`/invoices/${inv.id}/edit`, from)}
                      className="text-body-sm font-semibold text-accent-700 hover:underline"
                    >
                      Edit
                    </Link>
                  ) : undefined
                }
              />
              <CardBody>
                <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-body-sm text-secondary">{inv.notes}</p>
                    <p className="mt-1 text-caption text-grey-500">
                      The client reads this under “Notes on this invoice”.
                      {inv.state === 'DRAFT'
                        ? ' Edit the draft to change it.'
                        : ' It is part of an issued invoice and cannot be edited here.'}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

        </div>
      </div>

      <RecordPaymentModal invoice={inv} open={paymentOpen} onOpenChange={setPaymentOpen} />

      {/* The composer's mode follows the invoice, not the click that opened
          it: a draft can only be sent, an issued invoice can only be chased. */}
      <ComposerModal
        mode={inv.state === 'DRAFT' ? 'send' : composer === 'resend' ? 'resend' : 'remind'}
        invoice={inv}
        open={composer !== null}
        onOpenChange={(open) => setComposer(open ? (composer ?? 'remind') : null)}
        busy={send.isPending || logReminder.isPending || removeReminder.isPending}
        onSent={() => setComposer(null)}
        onCommit={(channel, payload) => {
          const to = payload.to || null;
          if (inv.state === 'DRAFT') {
            // One request: the delivery record and the DRAFT→OPEN flip are
            // written together, so an open invoice always knows who it went to.
            send.mutate(
              {
                id: inv.id,
                channel,
                to,
                subject: payload.subject || null,
                note: payload.note || null,
                // The date the composer quoted in the message it just drafted —
                // "keep Aug 5" must reach the invoice, not just the email.
                issueDate: payload.issueDate,
              },
              {
                onSuccess: (updated) => {
                  setComposer(null);
                  toast.success(
                    `Invoice #${inv.number} sent${to ? ` to ${to}` : ''} — ${
                      CHANNEL_VERBS[channel] ?? 'logged'
                    }, due ${formatDate(updated.dueDate)}`,
                  );
                  // "Copied" means the message is going out of an inbox we
                  // cannot reach, so the file has to follow it. A real send
                  // attaches the PDF itself and needs no download.
                  if (channel === 'copied') void savePdf({ quiet: true });
                },
                onError: (e) =>
                  toast.error(e instanceof Error ? e.message : 'Could not send the invoice'),
              },
            );
            return;
          }
          if (composer === 'resend') {
            logReminder.mutate(
              { id: inv.id, channel, to, subject: payload.subject || null, note: payload.note || null, kind: 'resend' },
              {
                onSuccess: () => {
                  setComposer(null);
                  toast.success(`Updated invoice #${inv.number} recorded as sent${to ? ` to ${to}` : ''}`);
                },
                onError: (e) =>
                  toast.error(e instanceof Error ? e.message : 'Could not record it'),
              },
            );
            return;
          }
          logReminder.mutate(
            { id: inv.id, channel, to, subject: payload.subject || null, note: payload.note || null },
            {
              onSuccess: (updated) => {
                const count = updated.chase.count;
                setComposer(null);
                toast.success(
                  `Reminder ${CHANNEL_VERBS[channel] ?? 'logged'} — chase ${count} of #${inv.number} recorded${
                    updated.chase.nextFollowUp
                      ? `, follow up ${formatDate(updated.chase.nextFollowUp)}`
                      : ''
                  }`,
                );
              },
              onError: (e) =>
                toast.error(e instanceof Error ? e.message : 'Could not log the reminder'),
            },
          );
        }}
        onRemoveReminder={(reminderId) =>
          removeReminder.mutate(
            { id: inv.id, reminderId },
            {
              onSuccess: () => toast.success('Reminder removed from the chase log'),
              onError: (e) =>
                toast.error(e instanceof Error ? e.message : 'Could not remove the reminder'),
            },
          )
        }
      />

      {/* Void confirmation.
          A partly-paid invoice is an accounting event, not a tidy-up: voiding
          it would leave money recorded against a document that no longer
          exists. The server refuses it, so the dialog says so BEFORE the click
          rather than returning a red error afterwards. */}
      <ScheduleSendDialog invoice={inv} open={scheduling} onOpenChange={setScheduling} />

      <ConfirmDialog
        open={confirmVoid && inv.paidAmount > 0}
        onOpenChange={setConfirmVoid}
        title="This invoice has been paid against"
        description={
          <>
            <span className="num font-semibold text-secondary">
              {formatMoney(inv.paidAmount)}
            </span>{' '}
            has been recorded against #{inv.number} across{' '}
            {plural(inv.payments.length, 'payment', 'payments')}
            {inv.payments[0] ? `, the first on ${formatDate(inv.payments[0].date)}` : ''}. Voiding
            it would leave that money against a cancelled invoice, so remove the payment
            {inv.payments.length === 1 ? '' : 's'} first — or record the balance and let the invoice
            close as paid.
            {releasedSummary(inv) && (
              <>
                {' '}
                Voiding it afterwards would then release{' '}
                {[
                  inv.linkedTimeEntryCount > 0
                    ? plural(inv.linkedTimeEntryCount, 'time entry', 'time entries')
                    : null,
                  inv.linkedExpenseCount > 0
                    ? plural(inv.linkedExpenseCount, 'expense', 'expenses')
                    : null,
                ]
                  .filter(Boolean)
                  .join(' and ')}{' '}
                back to unbilled.
              </>
            )}
          </>
        }
        confirmLabel="Show payments"
        cancelLabel="Leave it open"
        onConfirm={() => {
          setConfirmVoid(false);
          document
            .getElementById('invoice-payments')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
      />
      <ConfirmDialog
        open={confirmReceipt}
        onOpenChange={setConfirmReceipt}
        title="Re-send the receipt?"
        description={
          inv.client.billingEmail
            ? `The receipt for #${inv.number} (${formatMoney(inv.paidAmount)} paid${
                inv.paidDate ? ` on ${formatDate(inv.paidDate)}` : ''
              }) goes to ${[inv.client.billingEmail, ...(inv.client.billingEmailCc ?? [])].join(', ')}, with the invoice PDF attached.`
            : `${inv.client.name} has no billing email, so there is nowhere to send the receipt. Add one on the client first.`
        }
        confirmLabel="Send receipt"
        onConfirm={async () => {
          setActionError(null);
          try {
            const result = await api.post<{ to: string | null; status: string }>(`/invoices/${inv.id}/receipt`);
            toast.success(
              result.status === 'filed'
                ? `Receipt written to .mail/ (EMAIL_MODE=file) — not transmitted`
                : `Receipt for #${inv.number} sent to ${result.to}`,
            );
          } catch (e) {
            setActionError(e instanceof ApiError ? e.message : 'The receipt could not be sent');
            throw e;
          }
        }}
      />

      <ConfirmDialog
        open={confirmVoid && inv.paidAmount === 0}
        onOpenChange={setConfirmVoid}
        title="Void this invoice?"
        description={`Invoice #${inv.number} will be marked void${releasedSummary(inv)}. No payment has been recorded against it. This cannot be undone.`}
        confirmLabel="Void invoice"
        destructive
        onConfirm={async () => {
          setActionError(null);
          try {
            await voidInvoice.mutateAsync(inv.id);
            toast.success(`Invoice #${inv.number} voided${releasedSummary(inv)}`);
          } catch (e) {
            setActionError(e instanceof Error ? e.message : 'Could not void the invoice');
            throw e; // keep the dialog open on failure
          }
        }}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this draft?"
        description={`Draft invoice #${inv.number} will be permanently deleted${releasedSummary(inv)}.`}
        confirmLabel="Delete draft"
        destructive
        onConfirm={async () => {
          setActionError(null);
          try {
            await deleteInvoice.mutateAsync(inv.id);
            toast.success(`Draft #${inv.number} deleted`);
            navigate(from === 'client' ? `/clients/${inv.client.id}?tab=invoices` : invoiceListHref());
          } catch (e) {
            setActionError(e instanceof Error ? e.message : 'Could not delete the draft');
            throw e;
          }
        }}
      />

      {/* Remove payment confirmation */}
      <ConfirmDialog
        open={paymentToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPaymentToRemove(null);
        }}
        title="Remove this payment?"
        description={
          paymentToRemove
            ? `The ${formatMoney(paymentToRemove.amount)} payment recorded on ${formatDate(
                paymentToRemove.date,
              )} will be removed and the invoice balance and status recomputed${
                inv.state === 'PARTIAL' || inv.state === 'PAID'
                  ? ` (currently ${inv.state.toLowerCase()})`
                  : ''
              }.`
            : undefined
        }
        confirmLabel="Remove payment"
        destructive
        onConfirm={async () => {
          if (!paymentToRemove) return;
          setActionError(null);
          const removed = paymentToRemove;
          try {
            const updated = await removePayment.mutateAsync({
              id: inv.id,
              paymentId: removed.id,
            });
            toast.success(
              `${formatMoney(removed.amount)} payment removed — invoice is now ${updated.state.toLowerCase()}`,
            );
          } catch (e) {
            setActionError(e instanceof Error ? e.message : 'Could not remove the payment');
            throw e; // keep the dialog open on failure
          }
        }}
      />

      {printDoc.data && (
        <InvoicePreviewModal
          doc={printDoc.data}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          onDownloadPdf={() => void savePdf()}
          downloading={downloading === 'plain'}
          onPrint={() => {
            setPreviewOpen(false);
            // Let the dialog unmount before the print sheet takes over.
            window.setTimeout(() => window.print(), 120);
          }}
        />
      )}

      {printDoc.data && <InvoicePrintView doc={printDoc.data} />}
    </div>
  );
}
